<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import AuthModal from './auth/AuthModal.vue';
import Icon from './Icon.vue';
import { Target } from 'lucide-vue-next';

const logoUrl = new URL('../../branding/Logo-web.svg', import.meta.url).href;
const logoTextUrl = new URL('../../branding/Macumba Travel text.svg', import.meta.url).href;

// Props to track modal/section states
const props = defineProps({
  showTripFinderModal: {
    type: Boolean,
    default: false
  },
  showTripFinderSection: {
    type: Boolean,
    default: false
  },
  showBudgetEstimatorModal: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['toggle-menu', 'navigate', 'open-trip-finder', 'open-budget-estimator', 'toggle-trip-finder-section', 'open-trip-finder-section', 'close-trip-finder', 'close-budget-estimator']);
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// Mobile menu state
const showAuthModal = ref(false);
const authModalMode = ref('login');
const showUserMenu = ref(false);

// Authentication methods
const openAuthModal = (mode = 'login') => {
  authModalMode.value = mode;
  showAuthModal.value = true;
};

const closeAuthModal = () => {
  showAuthModal.value = false;
};

const handleLogin = () => {
  openAuthModal('login');
};

const handleRegister = () => {
  openAuthModal('register');
};

const handleLogout = async () => {
  await authStore.logout();
  showUserMenu.value = false;
  router.push('/');
};

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
};

const profilePhotoUrl = computed(() => {
  if (!authStore.isAuthenticated) return null;

  // Use profile_picture_url (signed URL from backend) first, then fall back to other fields
  const photo = authStore.user?.profile_picture_url || authStore.user?.profile_picture || authStore.user?.avatar_url || authStore.user?.avatar;
  if (photo) {
    // If it's already a full URL (signed URL), use as-is
    if (/^https?:\/\//i.test(photo)) {
      return photo;
    }
    // Otherwise, construct the uploads URL (proxied to backend in dev)
    return `/uploads/${photo}`;
  }

  // Default avatar for authenticated users without a profile photo
  return '/macumba-placeholder.svg';
});

const userFirstName = computed(() => {
  if (!authStore.isAuthenticated) return '';

  // Priority: Extract first word from Display Name (name field)
  const displayName = authStore.user?.name;
  if (displayName) {
    const firstWord = displayName.trim().split(/\s+/)[0];
    if (firstWord) return firstWord;
  }

  // Fallback to first_name or firstName fields
  return (
    authStore.user?.first_name ||
    authStore.user?.firstName ||
    authStore.user?.username ||
    ''
  );
});

const userLanguage = computed(() => {
  if (!authStore.isAuthenticated) return 'en';
  const lang = authStore.user?.language || authStore.user?.locale || '';
  if (lang) return String(lang).toLowerCase();
  return (navigator.language || 'en').toLowerCase();
});

const localizedHello = computed(() => {
  const lang = userLanguage.value;
  const base = lang.split('-')[0];

  if (base === 'es') return 'Hola';
  if (base === 'fr') return 'Bonjour';
  if (base === 'de') return 'Hallo';
  if (base === 'ja') return 'こんにちは';
  if (base === 'zh') return '你好';
  if (base === 'ko') return '안녕하세요';
  return 'Hello';
});

// Close user menu when clicking outside
const closeUserMenu = () => {
  showUserMenu.value = false;
};

// Auth bootstrap is owned by router.beforeEach — no onMounted initialize needed here.

// Navigation functions
const navigateToHome = () => {
  router.push('/');
};

const openTripFinder = () => {
  console.log('openTripFinder called', {
    currentRoute: route.path,
    budgetModalOpen: props.showBudgetEstimatorModal
  });

  // Close budget estimator modal if open
  if (props.showBudgetEstimatorModal) {
    emit('close-budget-estimator');
  }

  // Close any existing trip finder state
  emit('close-trip-finder');

  // Open trip finder as modal overlay on current page (no navigation)
  setTimeout(() => {
    console.log('openTripFinder called', { currentRoute: route.path, budgetModalOpen: props.showBudgetEstimatorModal });
    console.log('Opening trip finder after delay');
    openTripFinderAfterDelay();
  }, 50);
};

const openTripFinderAfterDelay = () => {
  // Check if we're on desktop (768px and above)
  const isDesktop = window.innerWidth >= 768;

  console.log('openTripFinderAfterDelay called', {
    isDesktop,
    currentRoute: route.path,
    windowWidth: window.innerWidth
  });

  // Always open the modal for both desktop and mobile
  console.log('Emitting open-trip-finder');
  emit('open-trip-finder');
};

const openBudgetEstimator = () => {
  // Toggle the budget estimator modal by emitting event to parent
  if (props.showBudgetEstimatorModal) {
    emit('close-budget-estimator');
  } else {
    // Close trip finder modal/section if open
    emit('close-trip-finder');
    emit('open-budget-estimator');
  }
};

const navigateToSavedTrips = () => {
  console.log('navigateToSavedTrips called', {
    currentRoute: route.path,
    budgetModalOpen: props.showBudgetEstimatorModal
  });

  // Close budget estimator modal if open
  emit('close-budget-estimator');

  // Close trip finder if open
  emit('close-trip-finder');

  console.log('Navigating to /saved-trips');
  router.push('/saved-trips');
};

// Active tab computed property
const activeTab = computed(() => {
  if (route.path === '/budget-estimator' || props.showBudgetEstimatorModal) return 'budget';
  if (route.path === '/saved-trips') return 'saved';
  if (props.showTripFinderModal || props.showTripFinderSection) return 'trip-finder';
  return null; // No active tab by default
});
</script>

<template>
  <header id="main-header" class="bg-white text-black fixed top-0 left-0 w-full z-50 shadow-none border-b border-gray-100">
    <div class="edge-padding">
      <div class="container-custom global-header__container py-2 pl-0 md:pl-0">
        <div class="flex items-center justify-between">
          <!-- Left side: Logo -->
          <div class="flex items-center">
            <button type="button" class="cursor-pointer ml-0 md:ml-[18px] flex items-center" @click="navigateToHome"
              aria-label="Go to home">
              <img :src="logoUrl" alt="Macumba Travel" class="h-8 md:h-10 w-auto" />
              <img :src="logoTextUrl" alt="Macumba Travel" class="hidden md:block h-[16px] w-auto ml-2" />
            </button>
          </div>

          <!-- Center: Main Navigation Tabs (Desktop) -->
          <div class="hidden md:flex items-center space-x-8">
            <button @click="openTripFinder"
              class="flex items-center space-x-2 px-4 py-2 text-base font-semibold antialiased transition-colors duration-200"
              :class="activeTab === 'trip-finder' ? 'text-[#4ECF84]' : 'text-gray-900 hover:text-gray-900'">
              <Icon name="search" class="w-2 h-2 md:w-5 md:h-5" />
              <span>Trip Finder</span>
            </button>

            <button @click="openBudgetEstimator"
              class="flex items-center space-x-2 px-4 py-2 text-base font-semibold antialiased transition-colors duration-200"
              :class="activeTab === 'budget' ? 'text-[#4ECF84]' : 'text-gray-900 hover:text-gray-900'">
              <Icon name="calculator" class="w-2 h-2 md:w-5 md:h-5" />
              <span>Budget Estimator</span>
            </button>

            <button
              @click="navigateToSavedTrips"
              class="flex items-center space-x-2 px-4 py-2 text-base font-semibold antialiased transition-colors duration-200"
              :class="activeTab === 'saved' ? 'text-[#4ECF84] border-b-2 border-[#4ECF84]' : 'text-gray-900 hover:text-gray-900'">
              <Icon name="map-plus" class="w-2 h-2 md:w-5 md:h-5" />
              <span>Trips</span>
            </button>
          </div>

          <!-- Right side: User Menu -->
          <div class="flex items-center space-x-4 md:pr-[18px]">
            <!-- Authentication Section -->
            <div class="relative">
              <!-- Authenticated User -->
              <div v-if="authStore.isAuthenticated" class="flex items-center space-x-2 md:space-x-4">
                <!-- Desktop: Welcome back [Name]! -->
                <div class="hidden md:flex items-center space-x-4 mr-2">
                  <span class="text-base text-gray-700">
                    Hi <router-link to="/profile"
                      class="font-medium hover:text-[#4ECF84] hover:no-underline no-underline">{{ userFirstName
                      }}</router-link>!
                  </span>
                </div>

                <!-- Mobile: Hello [Name] (Optional, or just keep profile button) -->
                <span class="md:hidden text-base mr-2">
                  {{ localizedHello }} {{ userFirstName }}
                </span>

                <button @click="toggleUserMenu"
                  class="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 hover:bg-gray-200 focus:outline-none transition-colors border-2 border-transparent hover:border-[#4ECF84]"
                  :aria-label="'User menu for ' + (authStore.user?.username || 'User')">
                  <template v-if="profilePhotoUrl">
                    <img :src="profilePhotoUrl" alt="User profile photo" class="w-full h-full object-cover" />
                  </template>
                  <template v-else>
                    <Icon name="user" class="w-6 h-6 md:w-7 md:h-7" />
                  </template>
                </button>

                <!-- User Dropdown Menu -->
                <div v-if="showUserMenu"
                  class="absolute right-0 top-12 w-64 bg-white rounded-[24px] shadow-xl py-6 z-50 border border-gray-100 overflow-hidden">
                  <!-- Authenticated: user info header -->
                  <div class="px-4 pb-2">
                    <div class="flex items-center space-x-3 mb-3 pb-3 border-b border-gray-100">
                      <div class="w-10 h-10 bg-gray-100 rounded-full overflow-hidden flex items-center justify-center shrink-0">
                        <template v-if="profilePhotoUrl">
                          <img :src="profilePhotoUrl" alt="User profile photo" class="w-full h-full object-cover" />
                        </template>
                        <template v-else>
                          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </template>
                      </div>
                      <div class="min-w-0">
                        <div class="font-medium text-gray-900 truncate">{{ authStore.user?.username }}</div>
                        <div class="text-sm text-gray-500 truncate">{{ authStore.user?.email }}</div>
                      </div>
                    </div>

                    <!-- TripHub -->
                    <router-link to="/triphub" @click="closeUserMenu"
                      class="flex items-center space-x-3 px-2 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 no-underline hover:no-underline">
                      <Target :size="20" :stroke-width="1.5" class="shrink-0" />
                      <span class="font-medium">TripHub</span>
                    </router-link>

                    <!-- Settings -->
                    <router-link to="/profile" @click="closeUserMenu"
                      class="flex items-center space-x-3 px-2 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 no-underline hover:no-underline">
                      <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span class="font-medium">Settings</span>
                    </router-link>

                    <!-- Admin Dashboard (admin only) -->
                    <router-link v-if="authStore.isAdmin" to="/admin" @click="closeUserMenu"
                      class="flex items-center space-x-3 px-2 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 border-t border-gray-100 no-underline hover:no-underline">
                      <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span class="font-medium">Admin Dashboard</span>
                    </router-link>

                    <!-- Sign Out -->
                    <button @click="handleLogout"
                      class="flex items-center space-x-3 px-2 py-3 w-full text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 border-t border-gray-100">
                      <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span class="font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Unauthenticated User -->
              <div v-else class="flex items-center space-x-3">
                <button @click="handleRegister"
                  class="hidden md:block px-3 py-1.5 text-base font-semibold antialiased text-white bg-[#4ECF84] hover:bg-[#4A9B7A] rounded-[3px] focus:outline-none transition-colors">
                  Register
                </button>
                <button @click="handleLogin"
                  class="flex items-center space-x-2 px-3 py-1.5 text-base font-semibold antialiased hover:text-gray-900 focus:outline-none">
                  <span>Sign in</span>
                  <Icon name="user-circle" class="w-8 h-8 md:w-6 md:h-6" stroke-width="1" color="#2F3033" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authentication Modal -->
    <AuthModal v-if="showAuthModal" :show="showAuthModal" :initial-mode="authModalMode" @close="closeAuthModal"
      @auth-success="closeAuthModal" />



    <!-- Backdrop for user menu -->
    <div v-if="showUserMenu" class="fixed inset-0 z-40" @click="closeUserMenu"></div>
  </header>
</template>

<style scoped>
/* Custom styles for active tab indicator */
.border-b-2 {
  border-bottom-width: 2px;
  border-bottom-style: solid;
}

@media (min-width: 768px) {
  .global-header__container {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

/* Green filter for selected search icon */
.filter-green {
  filter: brightness(0) saturate(100%) invert(67%) sepia(35%) saturate(1347%) hue-rotate(113deg) brightness(96%) contrast(89%);
}

/* Ensure no underlines on hover for header links */
.global-header__container a:hover,
.absolute.right-0.top-12 a:hover {
  text-decoration: none !important;
}
</style>
